import { SiteUrls } from '@tableau/shared-urls';
import { DataGrid } from '@tableau/widgets-datagrid';

import { CommonDataGridColumns } from '../../common/react/CommonDataGridColumns';
import { IActionTarget } from '../../common/react/components/ActionsMenu';
import { DataGridCells } from '../../common/react/components/dataGrid/DataGridCells';
import { SortableHeader } from '../../common/react/components/dataGrid/SortableHeader';
import { RightAlignedCell, RightAlignedHeader } from '../../common/react/components/dataGrid/StyledCellWrapper';
import { IReactTranslateService } from '../../common/react/IReactTranslateService';
import { serverScheduleUrl, siteScheduleUrl } from '../../schedule/utils/ScheduleUtils';
import { IScheduleActionNameFilter } from '../../ts/filters/ScheduleActionNameFilter';
import { IScheduleFrequencyDescriptionFilter } from '../../ts/filters/ScheduleFrequencyDescriptionFilter';
import { IScheduleTypeNameFilter } from '../../ts/filters/ScheduleTypeNameFilter';
import { ISchedule } from '../../ts/ServerApi/types/ISchedule';
import { IActionButton } from '../../ts/services/ActionPanelService';
import { ServerUrls } from '../ServerUrls';

export module ScheduleGrid {

  export function columns(
    translate: IReactTranslateService,
    scope: DataGridCells.ISelectedItemsScope<ISchedule>,
    actions: IActionButton<ISchedule, {}>[],
    getActionTarget: () => IActionTarget<ISchedule, {}>,
    isServerPage: boolean,
    serverUrls: ServerUrls,
    siteUrls: SiteUrls,
    scheduleTypeNameFilter: IScheduleTypeNameFilter,
    scheduleActionNameFilter: IScheduleActionNameFilter,
    scheduleFrequencyDescriptionFilter: IScheduleFrequencyDescriptionFilter,
    displayScheduleDescriptionAsName: boolean)
  : DataGrid.Column[] {

    const columns: DataGrid.Column[] = [
      CommonDataGridColumns.selected(scope),
      CommonDataGridColumns.withExtraSpace(nameColumn(translate, isServerPage, serverUrls, siteUrls, actions, getActionTarget, scope,
          scheduleFrequencyDescriptionFilter, displayScheduleDescriptionAsName)),
      {
        name: 'frequency-col',
        dataKey: 'scheduleType',
        headerText: translate('column_schedule_frequency'),
        direction: 'asc',
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.PlainTextCellWithTransform('scheduleType', scheduleTypeNameFilter)
      },
      {
        name: 'task-type-col',
        dataKey: 'scheduledAction',
        headerText: translate('column_schedule_type'),
        direction: 'asc',
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.PlainTextCellWithTransform('scheduledAction', scheduleActionNameFilter)
      },
      {
        name: 'task-count-col',
        dataKey: 'taskCount',
        headerText: translate('column_schedule_task_count'),
        headerFormatter: RightAlignedHeader(SortableHeader.Formatter()),
        cellFormatter: RightAlignedCell(DataGridCells.LocalizedCount('taskCount')),
        direction: 'asc',
      },
      {
        name: 'execution-col',
        dataKey: 'parallel',
        direction: 'asc',
        headerText: translate('column_schedule_behavior'),
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.PlainTextCellWithTransform('parallel', (parallel: boolean) => {
          return parallel ? translate('scheduleBehaviorParallel') : translate('scheduleBehaviorSerial');
        })
      },
      {
        name: 'next-run-at-col',
        dataKey: 'runNextAt',
        headerText: translate('column_schedule_next_run'),
        direction: 'asc',
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.DateTimeCell('runNextAt', translate('disabled'))
      }
    ];

    return columns;
  }

  function nameColumn(translate: IReactTranslateService,
                      isServerPage: boolean,
                      serverUrls: ServerUrls,
                      siteUrls: SiteUrls,
                      actions: IActionButton<ISchedule, {}>[],
                      getActionTarget: () => IActionTarget<ISchedule, {}>,
                      scope: DataGridCells.ISelectedItemsScope<ISchedule>,
                      scheduleFrequencyDescriptionFilter: IScheduleFrequencyDescriptionFilter,
                      displayScheduleDescriptionAsName: boolean) {
    return CommonDataGridColumns.withActions({
        name: 'name-col',
        dataKey: 'name',
        headerText: translate('column_name'),
        direction: 'asc',
        headerFormatter: SortableHeader.Formatter(),
        cellFormatter: DataGridCells.Link((row: DataGrid.Row) => {
          const schedule = row.data as ISchedule;
          let displayName = schedule.name;
          if (displayScheduleDescriptionAsName) {
            displayName = displayName + ' - ' + scheduleFrequencyDescriptionFilter(schedule);
          }
          return {
            text: displayName,
            url: isServerPage
              ? serverScheduleUrl(serverUrls, schedule.id, schedule.scheduledAction)
              : siteScheduleUrl(siteUrls, schedule.id, schedule.scheduledAction),
            iconClassName: 'tb-grid-icon-schedule',
            iconTitle: translate('schedule')
          };
        })
      },
      {
        actions: actions,
        getActionTarget: getActionTarget,
        translate: translate
      }, scope
    );
  }

}
